import React, { useState } from 'react'
import 'header-pattern/dist/foundation.css'
import { authService } from '../../services/auth/authService'
import { useLocation, useNavigate } from 'react-router-dom'
import megaMenuSignOffIcon from 'styleguide/icons/ds2/megamenu-icon-sign-out.svg'
import { useProfileDisplay } from '../../hooks/useProfileDisplay'
import { useRoleGuard } from '../../hooks/useRoleGaurd'

const Header: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isSigningOff, setIsSigningOff] = useState(false)

  // Get profile data from hook (reads from Redux only, no API call)
  const { userInfo } = useProfileDisplay()
  const { isAdmin, isAPAdmin, isFEAdmin, isRPAdmin, isRequestorProxy } = useRoleGuard([
    'admin',
    'apAdmin',
    'feAdmin',
    'rpAdmin',
    'requestorProxy',
  ])

  const getRoute = (pathname: string): string => {
    if (pathname.includes('/admin')) return 'admin'
    if (pathname.includes('/approver')) return 'approver'
    if (pathname.includes('/requestor')) return 'requestor'
    return 'requestor'
  };
 
  const handleLogoClick = () => {
    const pathname = location.pathname
    // Requestor-proxy-only users can't access /admin, so send them to their
    // actual admin-side home (manual-timeoff) instead of the denied landing page.
    if (pathname.includes('/admin')) {
      const hasAdminRole = isAdmin || isAPAdmin || isFEAdmin || isRPAdmin
      if (hasAdminRole) {
        navigate('/admin')
      } else if (isRequestorProxy) {
        navigate('/admin/manual-timeoff')
      } else {
        navigate('/requestor')
      }
    } else {
      navigate(`/${getRoute(pathname)}`)
    }
    setIsMobileMenuOpen(false);
  };
  
  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  const handleSignOff = async () => {
    if (isSigningOff) return
    setIsSigningOff(true)
    setIsMobileMenuOpen(false);
    await authService.logout()
  }

  return (
    <header
      className="ds-header relative z-[60]"
      data-ds-theme="vessel"
      data-ds-variant="compact"
      data-ds-version="2"
    >
      <div className="ds-header__layout !px-0 lg:mx-40! desktop:mx-[120px]! tablet:mx-5! mobile:mx-4!">
        <div className="ds-header__top-bar flex flex-1 items-center ">
          <div className="ds-header__branding flex items-center gap-0">
            <button type="button"
              className="ds-header__logo"
              data-analytics-type="image"
              data-analytics-click="kp-logo"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                 onClick={handleLogoClick}
            >
              <img
                className="ds-header__logo-img ds-header__logo-img--mobile block sm:hidden"
                src="/assets/images/KP_stacked_logo.png"
                width="150"
                height="40"
                alt="Kaiser Permanente Home - Mobile"
              />

              <img
                className="ds-header__logo-img ds-header__logo-img--desktop hidden sm:block"
                src="/assets/images/KP_logo.png"
                width="374"
                height="42"
                alt="Kaiser Permanente Home"
              />
            </button>
            <button  type="button" onClick={handleLogoClick} className="hidden p2 -book sm:block text-white text-sm ml-2 !px-5 !pt-[15px]" style={{ color: 'white', background: 'none', border: 'none', cursor: 'pointer' }}>| <span className='underline decoration-[0.8px]'>Time Off Request Tracker (TORT)</span></button>
          </div>

          {/* Mobile Hamburger Menu Button */}
          <button  type="button"
            className="sm:hidden flex flex-col items-center justify-center ml-auto mr-2 py-6 px-4 hover:bg-opacity-80 transition-all"
            onClick={handleMobileMenuToggle}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span className={`mt-6! mb-6! transition-all duration-300 ${isMobileMenuOpen ? 'hidden' : 'block'}`}>
              <svg className="w-6 h-6 text-white mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="text-white text-xs font-medium !font-[350]">Menu</span>
            </span>
            <span className={`mt-6! mb-6! transition-all duration-300 ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
              <svg className="w-6 h-6 text-white mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {/* <span className="text-white text-xs font-medium">Close</span> */}
            </span>
          </button>

          <div className="ds-header__actions hidden sm:flex ml-auto items-center flex-wrap gap-4 !my-2">
            <span className="text-white truncate">Welcome {userInfo}</span>
            <button  type="button"
              className="button -primary"
              onClick={handleSignOff}
              disabled={isSigningOff}
            >
              {isSigningOff ? 'Signing Off...' : 'Sign Off'}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <div className={`sm:hidden overflow-hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'max-h-48 opacity-100 !pt-6 !pb-6' : 'max-h-0 opacity-0'}`}>
          <nav className="px-4 py-3">
            <div className="space-y-3">
              <span className="block text-white text-sm font-medium truncate">Welcome {userInfo}</span>
              <button  type="button"
                className="w-full flex items-center gap-2 pt-4 text-white text-left text-sm hover:opacity-80 transition-all"
                onClick={handleSignOff}
                disabled={isSigningOff}
              >
                <img
                  src={megaMenuSignOffIcon}
                  alt=""
                  width="20"
                  height="20"
                />
                <span className="text-white text-sm">Sign Off</span>
              </button>
            </div>
          </nav>
        </div>
      </div>
    </header>
  )
}

export default Header